import { RefObject, useEffect } from "react";

/**
 * Start playback where the platform allows sound (desktop, after the
 * open-dialog user gesture); iOS blocks unmuted autoplay outright — retry
 * MUTED (always allowed) so playback still starts and the user unmutes via the
 * native controls. Without the fallback iOS shows a paused player on open.
 *
 * Keyed on `url` so swapping the source (swiping to the next video, or a
 * re-fetch) re-attempts. Shared by the small (VideoViewer) and large
 * (StreamedVideoViewer) players so the iOS behaviour is identical.
 */
export function useMutedAutoplayRetry(
  videoRef: RefObject<HTMLVideoElement | null>,
  url: string,
): void {
  useEffect(() => {
    const el = videoRef.current;
    if (!el) {
      return;
    }
    let cancelled = false;
    const attempt = el.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => {
        if (cancelled) {
          return;
        }
        el.muted = true;
        const retry = el.play();
        if (retry && typeof retry.catch === "function") {
          retry.catch(() => {
            // Still blocked — leave the paused player with its controls.
          });
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [videoRef, url]);
}
